import format from 'date-fns/format';
import { Injectable } from '@angular/core';
import { environment } from '@environment';
import { EventBusService } from '@shared/core';
import { DutyData, EventData, ScheduleData } from 'app/wfm/shared/models/types';
import { Events, NotificationEvent } from '@shared/core/types';
import { TranslocoService } from '@ngneat/transloco';
import { PersistentSetService } from './persistent-set.service';

const TIME_WINDOW_MS = 10 * 60 * 1000; // notify 10 minutes before the event
const REDIRECT_TO = environment.crm.frontend.host + '/wfm/operator/schedule/my';
const DEFAULT_TITLE = 'В работе';

interface NormalizedEvent {
  date: Date;
  title: string;
}

function isNotify(event: EventData): boolean {
  return event.type?.isNotify;
}

function normalizeEvent(event: EventData): NormalizedEvent {
  return {
    date: new Date(event.start),
    title: event.title,
  };
}

function normalizeTime(dutyData: DutyData): NormalizedEvent {
  return {
    date: new Date(dutyData.start),
    title: DEFAULT_TITLE,
  };
}

function isIn<T>(value: T, start: T, end: T): boolean {
  return value >= start && value <= end;
}

@Injectable({
  providedIn: 'root',
})
export class WfmOperatorScheduleReminderService {
  constructor(
    private readonly eventBus: EventBusService,
    private readonly shownReminders: PersistentSetService<number>,
    private readonly transloco: TranslocoService,
  ) {}

  /**
   * Process reminders for the provided schedule
   */
  process(schedule: ScheduleData): void {
    if (!schedule) {
      return;
    }

    const start = new Date();
    const end = new Date(start.getTime() + TIME_WINDOW_MS);
    const upcomingEvents = schedule.time_on_duty
      .map(normalizeTime)
      .concat(schedule.events.filter(isNotify).map(normalizeEvent))
      .filter(event => isIn(event.date, start, end));
    const uniqueEvents = new Map(upcomingEvents.map(e => [e.date, e])).values();

    for (const event of uniqueEvents) {
      const eventKey = event.date.getTime();
      if (!this.shownReminders.has(eventKey)) {
        this.eventBus.emit<NotificationEvent>(Events.NotificationEvent, {
          // TODO: translate
          title: this.transloco.translate('crmCoreModule.wfmActivityNotification', {
            activity: event.title,
            time: format(event.date, 'HH:mm'),
          }),
          url: REDIRECT_TO,
        });
        this.shownReminders.set(eventKey);
      }
    }
    this.deleteObsoleteReminders();
  }

  private deleteObsoleteReminders(): void {
    const threshold = new Date().getTime() - TIME_WINDOW_MS;
    this.shownReminders.deleteMany(time => time < threshold);
  }
}
